import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"

import api from "@api"
import hasAccess from "@api/hasAccess"
import { ROLES } from "@components/Domain/Constants"
import { getConfig } from "@components/Config"
import { getCustomerSlug } from "@components/Dwolla"
import { ORGANIZATION_STORAGE_KEY } from "@components/constants"

import extendIdentityOrganization from "./helpers/extendIdentityOrganization"

const consoleOrganizationId = getConfig("consoleOrganizationId")

const readMyOrganizationOperation = api.get("organizations/ReadMyOrganization")
const updateMyCustomerVerificationStatusOperation = api.get("transactions/UpdateMyCustomerVerificationStatus")

const defaultOrganizationId = sessionStorage.getItem(ORGANIZATION_STORAGE_KEY)

const AFTER_SWITCH_PATH = "/backstage"


const useIdentityStore = (authorizeOrganizationIdentity) => {
  const navigate = useNavigate()

  const [ identity, setIdentity ] = useState()
  const [ organizationId, setOrganizationId ] = useState(defaultOrganizationId)

  const updateIdentity = updatedIdentity =>
    setIdentity(prevIdentity => ({
      ...prevIdentity,
      ...extendIdentityOrganization(updatedIdentity),
    }))

  const isSourceController = sourceId => {
    if (!identity) {
      return false
    }

    const { customers = [] } = identity

    const sources = customers
      .reduce((acc, cur) => [...acc, ...(cur.sources || [])], [])

    const sourceIds = sources.map(source => source.id)
    const hasSourceId = sourceIds.includes(sourceId)

    return hasSourceId
  }

  const addIdentityCustomer = newCustomer =>
    updateIdentity({
      ...identity,
      customers: [
        ...identity.customers,
        newCustomer
      ]
    })

  const updateIdentityCustomer = updatedCustomer =>
    updateIdentity({
      ...identity,
      customers: [
        ...identity.customers
          .map(customer => ({
            ...customer,
            ...(customer.id === updatedCustomer?.id ? updatedCustomer : {})
          }))
      ]
    })

  const updateIdentityOrganization = (updatedOrganization, force = false) => {
    const newIdentityWithOrganizations = {
      ...identity,
      organizations: [
        ...identity.organizations
          .map(organization => {
            if (force) {
              return {
                ...(organization.id === updatedOrganization?.id ? updatedOrganization : organization)
              }
            }

            return {
              ...organization,
              ...(organization.id === updatedOrganization?.id ? updatedOrganization : {})
            }
          })
      ]
    }

    const newIdentity = {
      ...newIdentityWithOrganizations,
      ...extendIdentityOrganization(newIdentityWithOrganizations)
    }

    updateIdentity(newIdentity)
  }

  const updateIdentityCustomerSource = (customerId, updatedSource) => {
    const updatedCustomer = identity.customers.find(c => c.id === customerId) || {}
    const updatedSources = updatedCustomer.sources.map(source => {
      const isUpdatedSource = source.id === updatedSource?.id
      return isUpdatedSource ? updatedSource : source
    })

    updatedCustomer.sources = updatedSources

    updateIdentityCustomer(updatedCustomer)
  }

  const setBrokerReceiverSource = (receiverSourceId, receiverSourceName) =>
    updateIdentity({ ...identity, receiverSourceId, receiverSourceName })

  const addIdentityCustomerSource = (customerId, newOrExistingSource) => {
    const { customers } = identity
    const customerIndex = customers.findIndex(({ id }) => customerId === id)

    const { sources = [] } = customers[customerIndex]

    const isNewSource = sources
      .filter(source => source.id === newOrExistingSource.id)
      .length === 0

    if (isNewSource) {
      customers[customerIndex].sources = [...sources, newOrExistingSource]
    }

    updateIdentity({ ...identity, customers: [ ...customers ] })
  }

  const switchToOrganization = async (targetOrganizationId) => {
    const newIdentity = await authorizeOrganizationIdentity(targetOrganizationId)

    sessionStorage.setItem(ORGANIZATION_STORAGE_KEY, targetOrganizationId)
    setOrganizationId(targetOrganizationId)

    updateIdentity(newIdentity)
    navigate(AFTER_SWITCH_PATH)
  }

  const authorizeIdentity = (authenticationJwt) =>
    authorizeOrganizationIdentity(organizationId, authenticationJwt)

  const roles = identity?.roles || []
  const isBroker = roles.includes(ROLES.BROKER) && roles.length === 1
  const isInvestor = !!(identity?.investor)

  const updateIdentityMobile = (mobile) =>
    updateIdentity({ ...identity, mobile })

  const updateIdentityIs2FaEnabled = (is2FaEnabled) =>
    updateIdentity({ ...identity, is2FaEnabled })

  const getOrganization = () => {
    if (!identity) {
      return
    }

    return identity.organization
  }

  const getOrganizationEmail = () => {
    const organization = getOrganization()

    if (!organization) {
      return
    }

    return organization.email
  }

  const getOrganizationId = () => {
    if (!identity) {
      return
    }

    return identity.organization.id
  }

  const getOrganizationControllerId = () => {
    const organization = identity?.organization

    if (!organization) {
      return
    }

    const { controllerId } = organization

    return controllerId
  }

  const isOrganizationController = () => {
    const controllerId = getOrganizationControllerId()

    if (!identity) {
      return false
    }

    const { id: userId } = identity

    return userId === controllerId
  }

  const getCustomer = (customerName, impersonatedInvestorAccountId = false) => {
    const { customers } = identity

    const customerSlug = getCustomerSlug(customerName)

    if (!impersonatedInvestorAccountId) {
      return customers
        .find(item => getCustomerSlug(item.name) === customerSlug)
    }

    return customers
      .filter(customer => customer.investorAccountId === impersonatedInvestorAccountId)
      .find(item => getCustomerSlug(item.name) === customerSlug)
  }

  const useExtendedOrganization = request => {
    const [ isLoading, setIsLoading ] = useState(false)

    const { organization } = identity
    const { id, domainStatus } = organization

    let isInProgress = false
    useEffect(() => {
      if (isInProgress) { return }
      isInProgress = true

      const shouldExtendOrganization =
        !domainStatus && hasAccess(['org-read'])

      if (!shouldExtendOrganization) {
        return
      }

      setIsLoading(true)
      request(readMyOrganizationOperation.id)
        .then(({ data }) => updateIdentityOrganization(data))
        .then(() => setIsLoading(false))
    }, [ id, domainStatus ])

    return [ isLoading, organization ]
  }

  const updateMyCustomerVerificationStatus = async (authenticatedRequest, customerId) => {
    const { data } = await authenticatedRequest(updateMyCustomerVerificationStatusOperation.id, { id: customerId })
    updateIdentityCustomer(data)

    return data
  }

  const isConsoleOrganization = consoleOrganizationId === getOrganizationId()

  const organizations = identity?.organizations || []

  const hasConsoleAccess = !!organizations.find(({ id }) => id === consoleOrganizationId)

  return {
    setIdentity: updateIdentity,
    identity,
    isBroker,
    isInvestor,
    getCustomer,
    getOrganization,
    getOrganizationId,
    getOrganizationEmail,
    getOrganizationControllerId,
    authorizeIdentity,
    isSourceController,
    addIdentityCustomer,
    updateIdentityMobile,
    useExtendedOrganization,
    isOrganizationController,
    updateIdentityCustomerSource,
    updateIdentityCustomer,
    switchToOrganization,
    addIdentityCustomerSource,
    setBrokerReceiverSource,
    updateIdentityOrganization,
    updateIdentityIs2FaEnabled,
    updateMyCustomerVerificationStatus,
    isConsoleOrganization,
    hasConsoleAccess,
  }
}

export default useIdentityStore
